/**
 * support.js - where the prompt sits on the engine's ladder, and when it moves.
 *
 * PLATFORM SCAFFOLD. The ladder plan is the shape of every prompt sequence;
 * only the millisecond pacing and the words are a game's own.
 *
 * PURE. Indices and milliseconds in, indices and plans out.
 *
 * ---------------------------------------------------------------------------
 * THREE DIFFERENT MOVEMENTS, OFTEN CONFUSED
 *
 * 1. WITHIN a trial - the ladder walk. The trial opens at a rung and steps
 *    DOWN toward more support while the child does not answer. This is the
 *    signal that makes Phase 2 A, B and C three different measurements rather
 *    than one number written three times (COMPONENT-JUDGEMENT.md section 5).
 *    buildLadderPlan() below.
 *
 * 2. BETWEEN trials, downward - errorless learning. Section 8.4: a child under
 *    70% gets more prompting, automatically, and the ENGINE decides that. The
 *    game only obeys: applyRecommendation().
 *
 * 3. BETWEEN trials, upward - prompt fading. Section 2.5 requires support to
 *    be reduced systematically as the child succeeds. This game originally
 *    implemented that rule itself, because evaluateProgression() could raise
 *    support and never lower it (GAME-01.md section 7.2). **That is now an
 *    engine rule** - ACTIONS.REDUCE_SUPPORT, RULES.fade - and the game only
 *    obeys it, through the same applyRecommendation() that handles a raise.
 *    A fading rule that differed between games would mean a Green box meant
 *    something different in each of them (section 9.1).
 *
 * ---------------------------------------------------------------------------
 * THE LADDER RUNS BACKWARDS FROM INTUITION
 *
 * SUPPORT_LADDER index 0 is the MOST support (Pip says the word) and index 7
 * is none. So "increase support" decreases the index. Every helper here is
 * named for what it does to the CHILD, not to the number.
 */

import {
  SUPPORT_LADDER,
  SUPPORT_BY_ID,
  getSupportLevel,
  getPromptStyle,
} from '../../../engine/framework.js';
import { ACTIONS } from '../../../engine/progression.js';

/** The rung at which the target is simply given to the child. */
export const FULL_MODEL = 'full_model';

/** @param {number} phase @returns {{baseline:number, maxSupport:number, minSupport:number}} */
export function boundsFor(phase) {
  return getPromptStyle(phase).support;
}

/** Keep an index inside the phase's own range. Never leave the phase. */
export function clampRung(phase, index) {
  const { maxSupport, minSupport } = boundsFor(phase);
  return Math.max(maxSupport, Math.min(minSupport, index));
}

/** @param {number} index @returns {string} SUPPORT_LADDER id */
export function rungIdAt(index) {
  return getSupportLevel(index).id;
}

/** @param {string} rungId @returns {number} */
export function rungIndexOf(rungId) {
  const rung = SUPPORT_BY_ID[rungId];
  return rung ? rung.index : 0;
}

/** One step MORE supportive, clamped. */
export function moreSupport(phase, index) {
  return clampRung(phase, index - 1);
}

/** One step LESS supportive, clamped. */
export function lessSupport(phase, index) {
  return clampRung(phase, index + 1);
}

/** Is the game giving the child the answer at this rung? */
export function rungGivesTheAnswer(rungId) {
  return rungId === FULL_MODEL;
}

/**
 * Where a trial opens.
 *
 * Two claims have to be reconciled. The engine's placement (section 9.2) says
 * how much support this CHILD needs, from their colour on the chart. The
 * level's content says where this LEVEL sits in the phase - the spec's own
 * level intents ("Partial word shown", "Image highlight only") are rungs.
 *
 * The more supportive of the two wins. Section 2.4 is unambiguous that support
 * comes before failure, so where the two disagree the child's need is the one
 * to honour; the level intent is then reached by fading, which is what fading
 * is for.
 *
 * @param {object} input
 * @param {number} input.phase
 * @param {string} input.levelNominalRung  content nominalRung
 * @param {number} [input.placementIndex]  from progression.placeInGame()
 * @returns {number} ladder index
 */
export function openingRungIndex({ phase, levelNominalRung, placementIndex }) {
  const nominal = clampRung(phase, rungIndexOf(levelNominalRung));
  if (!Number.isInteger(placementIndex)) return nominal;
  return clampRung(phase, Math.min(nominal, placementIndex));
}

/**
 * The within-trial ladder walk.
 *
 * Every entry is relative to the END of the prompt, not to the start of the
 * trial: a child cannot begin responding until Pip has stopped talking, and
 * timing the wait from anywhere else punishes a slow synthesiser.
 *
 * `kind`:
 *   'hint'     Pip gives the semantic clue. Recorded as hintFired, which is
 *              what blocks Phase 2 component C and Phase 6 component D.
 *   'support'  the rung changes; recorded in supportSequence.
 *   'remodel'  the rung cannot go lower (the phase floor, or Phase 1 where
 *              there is only one rung), so Pip simply says it again. Recorded
 *              as another model, NOT as a rung change - inventing a rung the
 *              phase does not allow would corrupt every Phase 2 judgement.
 *
 * `models: true` on the LAST step, always. Errorless learning does not leave a
 * child with no answer, so the ladder ends with Pip saying the target however
 * far down the phase's rungs it was able to go.
 *
 * That is not the same as reaching the `full_model` RUNG, and the difference
 * caught a real bug in the first game built on this. Phase 3's bounds stop at
 * `partial_verbal` (index 1), so a Phase 3 ladder can never reach rung 0, so
 * `modelPlayed` was never true, so Phase 3 D - "produces without echoing" -
 * had nothing to be false about and silently scored 100%. A model is a thing
 * Pip DID; the rung is how much support was on screen. The trial record has
 * both fields precisely because they are different facts, and the spec says as
 * much at Phase 6: "Pip may model once" at the natural rung.
 *
 * @param {object} input
 * @param {number} input.phase
 * @param {number} input.openingIndex
 * @param {{hintAtMs:number, escalateAtMs:number[], windowMs:number}} input.pacing
 * @returns {Array<{atMs:number, kind:'hint'|'support'|'remodel', rungIndex?:number, rungId?:string}>}
 */
export function buildLadderPlan({ phase, openingIndex, pacing }) {
  const steps = [];
  const { maxSupport } = boundsFor(phase);

  if (Number.isFinite(pacing.hintAtMs) && pacing.hintAtMs < pacing.windowMs) {
    steps.push({ atMs: pacing.hintAtMs, kind: 'hint' });
  }

  let index = clampRung(phase, openingIndex);
  for (const atMs of pacing.escalateAtMs || []) {
    if (atMs >= pacing.windowMs) break;
    if (index > maxSupport) {
      index = moreSupport(phase, index);
      steps.push({ atMs, kind: 'support', rungIndex: index, rungId: rungIdAt(index) });
    } else {
      steps.push({ atMs, kind: 'remodel', rungIndex: index, rungId: rungIdAt(index) });
    }
  }

  steps.sort((a, b) => a.atMs - b.atMs);

  // The last thing that happens in a trial the child has not answered is that
  // they hear the answer. Whatever rung the phase allowed us to reach.
  for (let i = steps.length - 1; i >= 0; i -= 1) {
    if (steps[i].kind === 'hint') continue;
    steps[i].models = true;
    break;
  }

  return steps;
}

/**
 * Will the target be modelled on this trial if the child does not answer?
 *
 * Evidence for framework CONDITIONALS.model_opportunity: the engine excludes
 * Phase 3 D from the window on a trial where the answer was never on offer,
 * because "did not echo it" is free when there was nothing to echo. The game
 * reports its plan; it does not decide the box.
 *
 * @param {Array} plan       buildLadderPlan() output
 * @param {string} openingRungId
 * @returns {boolean}
 */
export function planModelsTarget(plan, openingRungId) {
  if (rungGivesTheAnswer(openingRungId)) return true;
  return (plan || []).some((step) => step.models);
}

/**
 * Obey the engine's pacing recommendation.
 *
 * Two actions move the rung, in opposite directions, and both are automatic:
 *
 *   increase_support  section 8.4 - the child is struggling. Support before
 *                     failure (section 2.4), so this is never offered.
 *   reduce_support    section 2.5 - the child has earned a step of
 *                     independence. Also never offered: the prompt level is
 *                     not something a child is asked about.
 *
 * `advance_offer` and `suggest_previous_level` are moves between LEVELS and
 * are the caller's to present - ENGINE-BUILD.md section 3.6 is explicit that
 * an offer must be offered, not taken.
 *
 * The engine computes the destination rung; the game only clamps it into the
 * phase, which the engine has already done, so the clamp is belt and braces.
 *
 * @param {{action:string, support?:{to:number}, reason?:string}} rec
 * @param {{phase:number, index:number}} current
 * @returns {{index:number, changed:boolean, direction:'up'|'down'|null, reason:string|null}}
 */
export function applyRecommendation(rec, { phase, index }) {
  const moves = rec
    && (rec.action === ACTIONS.INCREASE_SUPPORT || rec.action === ACTIONS.REDUCE_SUPPORT);
  if (!moves) return { index, changed: false, direction: null, reason: null };

  const fallback = rec.action === ACTIONS.INCREASE_SUPPORT
    ? moreSupport(phase, index)
    : lessSupport(phase, index);

  const to = rec.support && Number.isInteger(rec.support.to) ? rec.support.to : fallback;
  const next = clampRung(phase, to);

  return {
    index: next,
    changed: next !== index,
    // Named for what happens to the CHILD: 'up' is more independent.
    direction: next === index ? null : (next > index ? 'up' : 'down'),
    reason: next !== index ? rec.reason : null,
  };
}

/** Human-readable, for the grown-ups panel. */
export function describeRung(index) {
  const rung = getSupportLevel(index);
  return `${rung.name} - ${rung.detail}`;
}

export { SUPPORT_LADDER };

export default {
  FULL_MODEL,
  boundsFor,
  clampRung,
  rungIdAt,
  rungIndexOf,
  moreSupport,
  lessSupport,
  rungGivesTheAnswer,
  openingRungIndex,
  buildLadderPlan,
  planModelsTarget,
  applyRecommendation,
  describeRung,
};
